import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getOperationsKanban, updateTaskDueDate, updateTaskStatus, updateProjectStatus, updateProjectLifecycle } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Loader2, Target, Globe, Search, Sparkles, ArrowLeft,
  AlertTriangle, Clock, CheckCircle2, Calendar, GripVertical,
  Repeat, Archive,
} from "lucide-react";
import { TaskFreshnessPill } from "../components/TaskFreshnessPill";

const DEPT_ICONS = { marketing: Target, web_design: Globe, seo_aeo: Search, prospect_tools: Sparkles };
const DEPT_COLORS = {
  marketing: "bg-orange-500", web_design: "bg-blue-500", seo_aeo: "bg-green-500", prospect_tools: "bg-purple-500",
};

const KANBAN_COLUMNS = [
  { id: "todo", label: "To Do", statuses: ["pending"], color: "bg-zinc-100", headerColor: "text-zinc-700" },
  { id: "in_progress", label: "In Progress", statuses: ["in_progress", "blocked"], color: "bg-blue-50", headerColor: "text-blue-700" },
  { id: "completed", label: "Completed", statuses: ["completed"], color: "bg-green-50", headerColor: "text-green-700" },
];

function getColumnForStatus(status) {
  for (const col of KANBAN_COLUMNS) {
    if (col.statuses.includes(status)) return col.id;
  }
  return "todo";
}

function getStatusForColumn(colId) {
  const map = { todo: "pending", in_progress: "in_progress", completed: "completed" };
  return map[colId] || "pending";
}

export default function OperationsBoard() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDept, setActiveDept] = useState(null);

  const fetchData = async () => {
    try {
      const { data } = await getOperationsKanban();
      setDepartments(data);
    } catch (e) {
      console.error("Failed to fetch operations data:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const totalPending = departments.reduce((s, d) => s + d.total_pending, 0);
  const totalOverdue = departments.reduce((s, d) => s + d.total_overdue, 0);

  // Department drill-in Kanban view
  if (activeDept) {
    const dept = departments.find((d) => d.id === activeDept);
    if (!dept) { setActiveDept(null); return null; }
    return (
      <DepartmentKanban
        dept={dept}
        onBack={() => { setActiveDept(null); fetchData(); }}
        onRefresh={fetchData}
      />
    );
  }

  // Department overview
  return (
    <div className="space-y-6" data-testid="operations-board">
      <div>
        <h2 className="font-outfit text-xl font-semibold">Operations Board</h2>
        <p className="text-sm text-muted-foreground">
          {totalPending} pending tasks
          {totalOverdue > 0 && <span className="text-destructive font-semibold ml-2">{totalOverdue} overdue</span>}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {departments.map((dept) => {
          const Icon = DEPT_ICONS[dept.id] || Globe;
          const hasWork = dept.total_pending > 0;
          return (
            <Card
              key={dept.id}
              className={`border cursor-pointer transition-all hover:-translate-y-1 hover:shadow-md ${hasWork ? "" : "opacity-60"}`}
              onClick={() => setActiveDept(dept.id)}
              data-testid={`dept-${dept.id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2.5 rounded-lg ${DEPT_COLORS[dept.id]}`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{dept.name}</h3>
                    <p className="text-xs text-muted-foreground">{dept.clients.length} client{dept.clients.length !== 1 ? "s" : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-2xl font-bold">{dept.total_pending}</span>
                    <span className="text-xs text-muted-foreground">pending</span>
                  </div>
                  {dept.total_overdue > 0 && (
                    <div className="flex items-center gap-1.5 ml-auto">
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                      <span className="text-2xl font-bold text-red-600">{dept.total_overdue}</span>
                      <span className="text-xs text-red-600">overdue</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function DepartmentKanban({ dept, onBack, onRefresh }) {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "manager"].includes(user?.role);

  // Flatten all tasks from all clients, tag with client info
  const allTasks = [];
  dept.clients.forEach((client) => {
    client.tasks.forEach((task) => {
      allTasks.push({
        ...task,
        client_name: client.client_name,
        client_company: client.client_company,
        agency_name: client.agency_name,
        project_id: client.project_id,
      });
    });
  });

  // Also fetch completed tasks (they're not in the kanban data since we filter pending only)
  // We'll track locally moved tasks
  const [localTasks, setLocalTasks] = useState(allTasks);
  const [lifecyclePromptOpen, setLifecyclePromptOpen] = useState(false);
  const [pendingProject, setPendingProject] = useState(null);

  useEffect(() => {
    setLocalTasks(allTasks);
  }, [dept]);

  // Group into columns
  const columns = KANBAN_COLUMNS.map((col) => ({
    ...col,
    tasks: localTasks.filter((t) => getColumnForStatus(t.status) === col.id),
  }));

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const newStatus = getStatusForColumn(destination.droppableId);

    // Optimistic update
    setLocalTasks((prev) =>
      prev.map((t) => (t.id === draggableId ? { ...t, status: newStatus } : t))
    );

    try {
      await updateTaskStatus(draggableId, newStatus);
      toast.success(`Task moved to ${KANBAN_COLUMNS.find((c) => c.id === destination.droppableId)?.label}`);
      
      // After completing a task, check if ALL tasks of that project are now done → prompt
      if (newStatus === "completed" && isAdmin) {
        const movedTask = localTasks.find((t) => t.id === draggableId);
        if (movedTask?.project_id) {
          // Use updated task list after the optimistic move
          const projTasks = localTasks
            .map((t) => (t.id === draggableId ? { ...t, status: newStatus } : t))
            .filter((t) => t.project_id === movedTask.project_id);
          const allDone = projTasks.length > 0 && projTasks.every((t) => t.status === "completed");
          if (allDone) {
            try { await updateProjectStatus(movedTask.project_id, "completed"); } catch {}
            const projClient = dept.clients.find((c) => c.project_id === movedTask.project_id);
            setPendingProject({ id: movedTask.project_id, client_name: projClient?.client_name || movedTask.client_name });
            setLifecyclePromptOpen(true);
          }
        }
      }
    } catch (e) {
      toast.error("Failed to update task");
      // Revert
      setLocalTasks(allTasks);
    }
  };

  const handleDueDateChange = async (taskId, dueDate) => {
    try {
      await updateTaskDueDate(taskId, dueDate);
      toast.success("Due date updated");
    } catch (e) {
      toast.error("Failed to update due date");
    }
  };

  const Icon = DEPT_ICONS[dept.id] || Globe;

  return (
    <div className="space-y-4" data-testid={`kanban-${dept.id}`}>
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="kanban-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className={`p-2 rounded-lg ${DEPT_COLORS[dept.id]}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 className="font-outfit text-xl font-semibold">{dept.name}</h2>
          <p className="text-sm text-muted-foreground">
            {dept.total_pending} pending
            {dept.total_overdue > 0 && <span className="text-destructive ml-2">{dept.total_overdue} overdue</span>}
          </p>
        </div>
      </div>

      {/* Kanban Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-3 gap-4 min-h-[500px]">
          {columns.map((col) => (
            <Droppable droppableId={col.id} key={col.id}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`rounded-lg p-3 ${col.color} ${snapshot.isDraggingOver ? "ring-2 ring-primary/30" : ""}`}
                >
                  {/* Column Header */}
                  <div className="flex items-center justify-between mb-3 px-1">
                    <h3 className={`font-semibold text-sm ${col.headerColor}`}>{col.label}</h3>
                    <Badge variant="outline" className="text-xs">{col.tasks.length}</Badge>
                  </div>

                  {/* Task Cards */}
                  <ScrollArea className="max-h-[calc(100vh-300px)]">
                    <div className="space-y-2 min-h-[100px]">
                      {col.tasks.map((task, index) => (
                        <Draggable draggableId={task.id} index={index} key={task.id}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`bg-white rounded-lg border p-3 shadow-sm transition-shadow ${
                                snapshot.isDragging ? "shadow-lg ring-2 ring-primary/20" : "hover:shadow-md"
                              } ${task.is_overdue ? "border-red-300 bg-red-50" : ""}`}
                            >
                              <div className="flex items-start gap-2">
                                <div {...provided.dragHandleProps} className="mt-0.5 cursor-grab active:cursor-grabbing">
                                  <GripVertical className="h-4 w-4 text-zinc-300" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  {/* Client Name */}
                                  <p className="text-xs text-muted-foreground mb-1">
                                    {task.client_name}{task.agency_name && ` / ${task.agency_name}`}
                                  </p>
                                  {/* Task Title */}
                                  <p className={`text-sm font-medium leading-snug ${task.is_overdue ? "text-red-800" : ""}`}>
                                    {task.title}
                                  </p>
                                  {/* Meta row */}
                                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                                    <Badge variant="outline" className="text-[10px] h-5 px-1.5">
                                      {task.assignee_role === "va" ? "VA" : "RP"}
                                    </Badge>
                                    <TaskFreshnessPill status={task.status} dueDate={task.due_date} />
                                    <Link to={`/orders/${task.project_id}`} className="text-[10px] text-primary hover:underline ml-auto">
                                      View Order
                                    </Link>
                                  </div>
                                  {/* Due date */}
                                  <div className="mt-2">
                                    <Input
                                      type="date"
                                      value={task.due_date || ""}
                                      onChange={(e) => handleDueDateChange(task.id, e.target.value)}
                                      className="h-7 text-xs"
                                      data-testid={`due-date-${task.id}`}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {/* Lifecycle prompt — when ALL tasks for a project are completed */}
      <Dialog open={lifecyclePromptOpen} onOpenChange={setLifecyclePromptOpen}>
        <DialogContent className="max-w-md" data-testid="kanban-lifecycle-prompt">
          <DialogHeader>
            <DialogTitle>Order Completed — Where does it go next?</DialogTitle>
            <DialogDescription>All tasks for {pendingProject?.client_name || "this client"} are done. Pick the right bucket.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <button
              type="button"
              onClick={async () => {
                try {
                  await updateProjectLifecycle(pendingProject.id, "monthly_active");
                  toast.success("Moved to Monthly Active Clients");
                } catch { toast.error("Failed"); }
                setLifecyclePromptOpen(false);
                setPendingProject(null);
                onRefresh && onRefresh();
              }}
              data-testid="kanban-move-monthly"
              className="text-left p-4 border-2 rounded-lg hover:border-emerald-400 hover:bg-emerald-50 transition-all"
            >
              <div className="flex items-center gap-2 font-semibold text-emerald-800 mb-1">
                <Repeat className="h-5 w-5" /> Monthly Active Client
              </div>
              <p className="text-sm text-muted-foreground ml-7">Recurring monthly work — SEO, content, marketing campaigns.</p>
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  await updateProjectLifecycle(pendingProject.id, "archived");
                  toast.success("Moved to Archived Clients");
                } catch { toast.error("Failed"); }
                setLifecyclePromptOpen(false);
                setPendingProject(null);
                onRefresh && onRefresh();
              }}
              data-testid="kanban-move-archived"
              className="text-left p-4 border-2 rounded-lg hover:border-zinc-400 hover:bg-zinc-50 transition-all"
            >
              <div className="flex items-center gap-2 font-semibold text-zinc-800 mb-1">
                <Archive className="h-5 w-5" /> Archived Client
              </div>
              <p className="text-sm text-muted-foreground ml-7">One-time project complete. Reps can re-order anytime.</p>
            </button>
          </div>
          <Button variant="ghost" onClick={() => setLifecyclePromptOpen(false)} className="w-full">Decide later</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
